import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner@2.0.3';
import { format } from 'date-fns';
import { useAccountStore } from '../../../entities/account';
import {
  getRecentMedia,
  getMediaInsights,
} from '../../../shared/api/reports.api';
import { publicationsApi } from '../../../shared/api/publications.api';
import { PUBLICATION_CATEGORIES } from '../../../shared/lib/publicationCategories';
import type { PublicationCategory } from '../../../shared/types/common';
import { getTikTokAccounts, getTikTokVideosInRange } from '../../../shared/api/tiktok.api';
import { PlatformBadge } from '../ui/PlatformBadge';
import { mapWithConcurrency } from '../../../shared/lib/mapWithConcurrency';
import { DateRangeSelector } from './DateRangeSelector';
import { useDateRangeParams } from '../hooks/useDateRangeParams';
import { periodDays } from '../lib/date-range';

// Проверено вживую 07.10.2026: 8 одновременных запросов insights к одному
// аккаунту лимитов не задевают.
const ACCOUNT_CONCURRENCY = 4;
const INSIGHT_CONCURRENCY = 8;

import { useAverageViewsCache, type CategoryFilter } from '../store/averageViewsCache';
import { fetchAverageViews } from '../api/account-report';
import {
  buildAccountResult,
  calcStats,
  categoryOf,
  categorySummary,
  type AccountViewsResult,
  type ViewsItem,
} from '../lib/average-views';

const CATEGORY_CONCURRENCY = 8;

function categoryLabel(key: string): string {
  if (key === 'unknown') return 'Без категории';
  return PUBLICATION_CATEGORIES[key as PublicationCategory]?.label ?? key;
}

// Категории, поставленные вручную при публикации через InstaFarm: mediaId → тип.
async function loadManualCategories(accountId: string): Promise<Map<string, PublicationCategory>> {
  const map = new Map<string, PublicationCategory>();
  try {
    const publications = await publicationsApi.getByAccount(accountId);
    for (const pub of publications) {
      if (pub.category && pub.mediaId) map.set(pub.mediaId, pub.category);
    }
  } catch (err) {
    console.warn(`[Publications Analytics] Could not load categories for ${accountId}:`, err);
  }
  return map;
}

export function AverageViewsTab(): JSX.Element {
  const { accounts } = useAccountStore();
  // Посчитанное, ход расчёта и выбранная категория живут в сторе: расчёт идёт
  // минутами, и возвращение на вкладку должно заставать его на месте.
  // Период — общий для раздела.
  const [periodOption, setPeriodOption] = useDateRangeParams();
  const period = periodDays(periodOption);
  const periodLabel = `${format(period.from, 'dd.MM.yyyy')} — ${format(period.to, 'dd.MM.yyyy')}`;
  const computedFor = useAverageViewsCache((s) => s.computedFor);
  const setComputedFor = useAverageViewsCache((s) => s.setComputedFor);
  const reelsResults = useAverageViewsCache((s) => s.results) as any[];
  const setReelsResults = useAverageViewsCache((s) => s.setResults);
  const selectedCategory = useAverageViewsCache((s) => s.category);
  const setSelectedCategory = useAverageViewsCache((s) => s.setCategory);

  const isLoadingReels = useAverageViewsCache((s) => s.isLoading);
  const setIsLoadingReels = useAverageViewsCache((s) => s.setLoading);
  const reelsLoadingProgress = useAverageViewsCache((s) => s.progress);
  const setReelsLoadingProgress = useAverageViewsCache((s) => s.setProgress);

  const handleGetReelsAnalytics = async () => {
    // Флаг из стора: пока считается прошлый запуск, второй не начинаем.
    if (useAverageViewsCache.getState().isLoading) return;
    const reelsDateRange = { from: period.from, to: period.to };
    setIsLoadingReels(true);

    const activeAccounts = accounts.filter(acc => acc.isActive);
    let tiktokAccounts: { openId: string; username: string; displayName: string }[] = [];
    try {
      tiktokAccounts = await getTikTokAccounts();
    } catch {
      tiktokAccounts = [];
    }
    if (activeAccounts.length === 0 && tiktokAccounts.length === 0) {
      toast.error('Нет активных аккаунтов');
      setIsLoadingReels(false);
      return;
    }

    setReelsResults([]);
    setComputedFor(periodLabel);
    setSelectedCategory('all');
    setReelsLoadingProgress('Инициализация...');

    try {
      const accumulated: AccountViewsResult[] = [];
      const accountOrder = new Map(activeAccounts.map((a, i) => [a.id, i]));
      const byAccountOrder = (a: { accountId: string }, b: { accountId: string }) =>
        (accountOrder.get(a.accountId) ?? Infinity) - (accountOrder.get(b.accountId) ?? Infinity);
      const push = (result: AccountViewsResult | null) => {
        if (!result) return;
        accumulated.push(result);
        setReelsResults((prev) => [...prev, result].sort(byAccountOrder));
      };
      const meta = (account: (typeof activeAccounts)[number]) => ({
        accountId: account.id,
        platform: 'instagram' as const,
        username: account.username || account.name,
        name: account.name,
      });

      setReelsLoadingProgress(`Найдено ${activeAccounts.length} активных аккаунтов`);

      // Ручные категории — параллельно с подсчётом, они нужны только для разбивки.
      const categoriesPromise = (async () => {
        const maps = new Map<string, Map<string, PublicationCategory>>();
        await mapWithConcurrency(activeAccounts, CATEGORY_CONCURRENCY, async (account) => {
          maps.set(account.id, await loadManualCategories(account.id));
        });
        return maps;
      })();

      // 1) Сервер: все аккаунты одним запросом, метрики прямо в ленте.
      const done = new Set<string>();
      const serverPosts = new Map<string, { id: string; views: number; b2g: boolean }[]>();
      try {
        await fetchAverageViews(
          { accountIds: activeAccounts.map((a) => a.id), from: reelsDateRange.from, to: reelsDateRange.to },
          {
            onAccount: (accountId, posts) => {
              serverPosts.set(accountId, posts);
              done.add(accountId);
              setReelsLoadingProgress(`Instagram: обработано аккаунтов ${done.size}/${activeAccounts.length}`);
            },
            onAccountError: (accountId, message) => {
              done.add(accountId);
              const acc = activeAccounts.find((a) => a.id === accountId);
              toast.error(`Ошибка при обработке аккаунта @${acc?.username || accountId}: ${message}`);
            },
          },
        );
      } catch (err) {
        console.warn('[Publications Analytics] server path failed, falling back to browser:', err);
      }

      const categories = await categoriesPromise;
      for (const account of activeAccounts) {
        const posts = serverPosts.get(account.id);
        if (!posts) continue;
        const manual = categories.get(account.id) ?? new Map();
        push(buildAccountResult(
          meta(account),
          posts.map((p): ViewsItem => ({ views: p.views, category: categoryOf(p.id, p.b2g, manual) })),
        ));
      }

      // 2) Браузер — для аккаунтов, которые сервер не посчитал (старый бэкенд,
      // обрыв связи). Цифры те же: views, а при нуле — reach.
      const rest = activeAccounts.filter((a) => !done.has(a.id));
      let restDone = 0;
      await mapWithConcurrency(rest, ACCOUNT_CONCURRENCY, async (account) => {
        const accountName = account.username || account.name;
        try {
          const allMedia = await getRecentMedia(
            account.accessToken,
            account.instagramUserId,
            5000,
            { from: reelsDateRange.from, to: reelsDateRange.to }
          );
          if (allMedia.length === 0) return;
          const settled = await mapWithConcurrency(allMedia, INSIGHT_CONCURRENCY, (media) =>
            getMediaInsights(account.accessToken, media.id, media.media_type, media.media_product_type)
          );
          const manual = categories.get(account.id) ?? new Map();
          const items: ViewsItem[] = [];
          settled.forEach((r, i) => {
            if (!r.ok) {
              console.error(`[Publications Analytics] Error getting insights for media ${allMedia[i].id}:`, r.error);
              return;
            }
            const viewsOrReach = r.value.views || r.value.reach;
            if (viewsOrReach === undefined || viewsOrReach === null) return;
            items.push({ views: viewsOrReach, category: categoryOf(allMedia[i].id, false, manual) });
          });
          push(buildAccountResult(meta(account), items));
        } catch (error) {
          console.error(`[Publications Analytics] ❌ Error processing account ${accountName}:`, error);
          toast.error(`Ошибка при обработке аккаунта @${accountName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
          restDone++;
          setReelsLoadingProgress(`Instagram (из браузера): обработано аккаунтов ${restDone}/${rest.length}`);
        }
      });

      // TikTok accounts: average video views over the period. No categories in
      // the Display API, so all views land under "unknown".
      let ttIndex = 0;
      for (const tt of tiktokAccounts) {
        ttIndex++;
        const ttName = tt.username || tt.displayName;
        setReelsLoadingProgress(`TikTok [${ttIndex}/${tiktokAccounts.length}] @${ttName}...`);
        try {
          const vids = await getTikTokVideosInRange(
            tt.openId,
            reelsDateRange.from.getTime(),
            reelsDateRange.to.getTime() + 24 * 60 * 60 * 1000 - 1,
          );
          push(buildAccountResult(
            { accountId: `tt:${tt.openId}`, platform: 'tiktok', username: tt.username || tt.displayName, name: tt.displayName },
            vids.map((v): ViewsItem => ({ views: v.viewCount, category: 'unknown' })),
          ));
        } catch (error) {
          toast.error(`TikTok @${ttName}: ${error instanceof Error ? error.message : 'ошибка'}`);
        }
      }

      setReelsLoadingProgress('Завершение...');

      if (accumulated.length > 0) {
        const totalPublications = accumulated.reduce((sum, r) => sum + r.reelsCount, 0);
        toast.success(`✅ Аналитика получена для ${accumulated.length} аккаунтов (${totalPublications} публикаций)`);
        console.log(`[Publications Analytics] ✅ COMPLETED: ${accumulated.length} accounts, ${totalPublications} total publications`);
      } else {
        toast.error('⚠️ Не найдено публикаций за указанный период');
        console.warn('[Publications Analytics] ⚠️ No publications found for any account');
      }

    } catch (error) {
      console.error('[Publications Analytics] ❌ Fatal error:', error);
      toast.error(`Ошибка при получении аналитики: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingReels(false);
      setReelsLoadingProgress('');
    }
  };

  const categoryCounts = useMemo(() => {
    const counts: Partial<Record<CategoryFilter, number>> = { all: 0 };
    for (const result of reelsResults) {
      counts.all = (counts.all ?? 0) + result.reelsCount;
      for (const [cat, views] of Object.entries(result.byCategory ?? {})) {
        const key = cat as CategoryFilter;
        counts[key] = (counts[key] ?? 0) + (views as number[]).length;
      }
    }
    return counts;
  }, [reelsResults]);

  const filteredResults = useMemo(() => {
    if (selectedCategory === 'all') return reelsResults;
    return reelsResults
      .map(r => {
        const views: number[] = r.byCategory?.[selectedCategory] ?? [];
        const stats = calcStats(views);
        if (!stats) return null;
        return {
          ...r,
          reelsCount: stats.count,
          averageMean: stats.mean,
          averageMedian: stats.median,
          totalViews: stats.total,
          minViews: stats.min,
          maxViews: stats.max,
          allViews: views,
        };
      })
      .filter(Boolean);
  }, [reelsResults, selectedCategory]);

  const hasMultipleCategories = Object.keys(categoryCounts).length > 1;
  const summary = useMemo(() => categorySummary(reelsResults), [reelsResults]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Средние просмотры публикаций</CardTitle>
          <CardDescription>
            Анализ среднего количества просмотров/охвата всех публикаций (посты, Reels, карусели) за выбранный период
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Период</Label>
            <div>
              <DateRangeSelector value={periodOption} onChange={setPeriodOption} disabled={isLoadingReels} />
            </div>
          </div>

          <Button
            onClick={handleGetReelsAnalytics}
            disabled={isLoadingReels}
            className="w-full"
          >
            {isLoadingReels && <Loader2 className="h-4 w-4 animate-spin" />}
            Получить аналитику
          </Button>

          {reelsLoadingProgress && (
            <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
              {reelsLoadingProgress}
            </div>
          )}
        </CardContent>
      </Card>

      {reelsResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Результаты аналитики публикаций</CardTitle>
            <CardDescription>
              Средние просмотры/охват за период {computedFor ?? periodLabel}
              {computedFor && computedFor !== periodLabel && (
                <span className="block text-amber-700 dark:text-amber-400">
                  Период сменили после расчёта — «Получить аналитику», чтобы пересчитать за {periodLabel}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasMultipleCategories && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                >
                  Все ({categoryCounts.all ?? 0})
                </Button>
                {Object.entries(PUBLICATION_CATEGORIES).map(([key, cat]) => {
                  const count = categoryCounts[key as PublicationCategory] ?? 0;
                  return (
                    <Button
                      key={key}
                      variant={selectedCategory === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory(key as PublicationCategory)}
                    >
                      {cat.label} ({count})
                    </Button>
                  );
                })}
                {(categoryCounts.unknown ?? 0) > 0 && (
                  <Button
                    variant={selectedCategory === 'unknown' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory('unknown')}
                    className="text-muted-foreground"
                  >
                    Без категории ({categoryCounts.unknown})
                  </Button>
                )}
              </div>
            )}

            {summary.length > 1 && (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {summary.map((s) => (
                  <div key={s.category} className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">
                      {categoryLabel(s.category)} · {s.count} публ.
                    </div>
                    <div className="mt-1 text-sm">
                      среднее <span className="font-semibold tabular-nums">{s.mean.toLocaleString('ru-RU')}</span>
                      {' · '}медиана <span className="font-semibold tabular-nums">{s.median.toLocaleString('ru-RU')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Аккаунт</TableHead>
                    <TableHead className="text-right">Кол-во публикаций</TableHead>
                    <TableHead className="text-right">Медиана</TableHead>
                    <TableHead className="text-right">Среднее</TableHead>
                    <TableHead className="text-right">Мин</TableHead>
                    <TableHead className="text-right">Макс</TableHead>
                    <TableHead className="text-right">Всего</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.map((result) => (
                    <TableRow key={result.accountId}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          @{result.username}
                          <PlatformBadge platform={result.platform === 'tiktok' ? 'tiktok' : 'instagram'} />
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {result.reelsCount}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-primary">
                        {result.averageMedian.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        {result.averageMean.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {result.minViews.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {result.maxViews.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {result.totalViews.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-muted/50">
                    <TableCell>
                      <span className="font-semibold">Итого ({filteredResults.length} аккаунтов)</span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {filteredResults.reduce((sum, r) => sum + r.reelsCount, 0)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      {(() => {
                        const allViews = filteredResults.flatMap(r => r.allViews);
                        if (allViews.length === 0) return '—';
                        const sorted = [...allViews].sort((a, b) => a - b);
                        const mid = Math.floor(sorted.length / 2);
                        const median = sorted.length % 2 === 0
                          ? (sorted[mid - 1] + sorted[mid]) / 2
                          : sorted[mid];
                        return Math.round(median).toLocaleString();
                      })()}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-green-600">
                      {(() => {
                        const totalPublications = filteredResults.reduce((sum, r) => sum + r.reelsCount, 0);
                        const totalViews = filteredResults.reduce((sum, r) => sum + r.totalViews, 0);
                        return totalPublications > 0 ? Math.round(totalViews / totalPublications).toLocaleString() : '—';
                      })()}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {filteredResults.length > 0 ? Math.min(...filteredResults.map(r => r.minViews)).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {filteredResults.length > 0 ? Math.max(...filteredResults.map(r => r.maxViews)).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {filteredResults.reduce((sum, r) => sum + r.totalViews, 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
